import { clsx } from "clsx";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from "react";
import { Link } from "react-router";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold transition active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 select-none";
const variants: Record<Variant, string> = {
  primary: "bg-rust text-white shadow-soft hover:bg-rust-700 hover:shadow-lift",
  secondary: "bg-white text-ink ring-1 ring-line hover:ring-rust/40 hover:text-rust",
  ghost: "text-ink hover:bg-sand",
  danger: "bg-white text-rust ring-1 ring-rust/30 hover:bg-rust-50",
};
const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-5 text-[0.95rem]",
  lg: "h-14 px-7 text-base",
};

export function buttonClass(variant: Variant = "primary", size: Size = "md", className?: string) {
  return clsx(base, variants[variant], sizes[size], className);
}

export function Button({
  variant = "primary",
  size = "md",
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean }) {
  return (
    <button className={buttonClass(variant, size, className)} disabled={disabled || loading} {...rest}>
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

export function LinkButton({
  variant = "primary",
  size = "md",
  className,
  ...rest
}: ComponentProps<typeof Link> & { variant?: Variant; size?: Size }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />;
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={clsx("eyebrow text-rust", className)}>{children}</p>;
}

export function SectionHeading({ eyebrow, title, intro, center }: { eyebrow: string; title: ReactNode; intro?: ReactNode; center?: boolean }) {
  return (
    <div className={clsx("max-w-2xl", center && "mx-auto text-center")}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-3 font-display text-4xl leading-tight text-ink sm:text-5xl">{title}</h2>
      {intro && <p className="mt-4 text-lg text-muted">{intro}</p>}
    </div>
  );
}

export function Container({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx("mx-auto w-full max-w-6xl px-5 sm:px-8", className)}>{children}</div>;
}

export function PoleBar({ className, animated }: { className?: string; animated?: boolean }) {
  return <div aria-hidden className={clsx("pole-stripes h-1.5 w-full", animated && "animate-pole", className)} />;
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-3 py-10 text-muted">
      <span className="pole-stripes block h-2 w-16 animate-pole rounded-full" aria-hidden />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function Card({ children, className, ...rest }: ComponentProps<"div">) {
  return (
    <div className={clsx("rounded-[var(--radius-card)] bg-white shadow-soft ring-1 ring-line/70", className)} {...rest}>
      {children}
    </div>
  );
}

export function Alert({ tone = "info", children }: { tone?: "info" | "error" | "success"; children: ReactNode }) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={clsx(
        "rounded-2xl px-4 py-3 text-sm",
        tone === "error" && "bg-rust-50 text-rust-800 ring-1 ring-rust/20",
        tone === "info" && "bg-sand text-ink ring-1 ring-line",
        tone === "success" && "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200",
      )}
    >
      {children}
    </div>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-ink">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-sm text-rust">{error}</span> : hint ? <span className="mt-1 block text-sm text-muted">{hint}</span> : null}
    </label>
  );
}

export const inputClass =
  "block w-full rounded-xl border-0 bg-white px-4 py-3 text-base text-ink ring-1 ring-line placeholder:text-steel focus:ring-2 focus:ring-rust focus:outline-none";
